import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  IPackagingReturnDetailedItem,
  IPackagingReturnListItem,
} from 'src/types/packaging-return';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  returns: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IPackagingReturnListItem[];
  };
  return: IPackagingReturnDetailedItem | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  returns: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  return: null,
};

const packagingReturnSlice = createSlice({
  name: 'packagingReturn',
  initialState,
  reducers: {
    removePackagingReturn: (state, action) => {
      const { returnId } = action.payload;

      const index = state.returns.items.findIndex((item) => item._id === returnId);

      if (index > -1) {
        action.payload.index = index;
        state.returns.items = state.returns.items.filter((item) => item._id !== returnId);
      }
    },
    rollbackPackagingReturnDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.returns.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPackagingReturns.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingReturns.fulfilled, (state, action) => {
        state.status = 'success';
        state.returns = action.payload;
      })
      .addCase(getPackagingReturns.rejected, (state, action) => {
        state.returns = initialState.returns;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getPackagingReturnById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingReturnById.fulfilled, (state, action) => {
        state.status = 'success';
        state.return = action.payload;
        state.error = initialState.error;
      })
      .addCase(getPackagingReturnById.rejected, (state, action) => {
        state.return = initialState.return;
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default packagingReturnSlice.reducer;

export const { removePackagingReturn, rollbackPackagingReturnDeletion } =
  packagingReturnSlice.actions;

// sourceDispatch/packagingItem/product filters were dropped — the header
// list has no line-level fields to filter by now.
export const getPackagingReturns = createAsyncThunk(
  'packagingReturn/paginated',
  async (params: {
    page: number;
    limit: number;
    generalSearch?: string | null;
    issuedBy?: string | null;
    receivedBy?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const { page, limit, generalSearch, issuedBy, receivedBy, startDate, endDate, sortBy, sort } =
      params;

    try {
      const response = await axios.get(
        `packaging/return?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&issuedBy=${issuedBy ?? ''}&receivedBy=${receivedBy ?? ''}&startDate=${
          startDate ? startDate.toISOString() : ''
        }&endDate=${endDate ? endDate.toISOString() : ''}&sortBy=${sortBy}&sort=${sort ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getPackagingReturnById = createAsyncThunk(
  'packagingReturn/getById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`packaging/return/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deletePackagingReturn = createAsyncThunk(
  'packagingReturn/delete',
  async (params: { item: IPackagingReturnListItem; index: number }, { dispatch }) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(`packaging/return/${item._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackPackagingReturnDeletion({ item, index }));
      throw err;
    }
  }
);

export interface TPackagingReturnItemInput {
  sourceDispatchItem: string;
  quantity: number;
  // Employee physically returning this specific line — per-line, not
  // per-return.
  issuedBy: string;
  // Create-only — a return with damaged lines can't be edited (the backend
  // refuses it since the auto-created linked damage dispatch has no
  // diffing/re-sync logic).
  damaged?: boolean;
  supplierDamagedQuantity?: number;
  staffDamagedQuantity?: number;
}

export interface TCreatePackagingReturn {
  receivedBy: string;
  returnDate: Date;
  remarks: string | null;
  items: TPackagingReturnItemInput[];
}

export type TUpdatePackagingReturn = TCreatePackagingReturn;

export const createPackagingReturn = createAsyncThunk(
  'packagingReturn/create',
  async (params: { returnTransaction: TCreatePackagingReturn }) => {
    const { returnTransaction } = params;
    const response = await axios.post('packaging/return', returnTransaction);
    return response.data.data;
  }
);

export const updatePackagingReturn = createAsyncThunk(
  'packagingReturn/update',
  async (params: { returnId: string; returnTransaction: TUpdatePackagingReturn }) => {
    const { returnId, returnTransaction } = params;
    const response = await axios.put(`packaging/return/${returnId}`, returnTransaction);
    return response.data.data;
  }
);
